import { FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { AuthShell, AuthSubmit } from "@/components/AuthShell";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const match = password.length > 0 && password === confirm;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("Missing reset token.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await authApi.resetPassword(token, password);
      window.__toast?.("Password updated successfully!", "ok");
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthShell subtitle="All set">
        <div className="text-center">
          <div className="mb-4 text-4xl">🎉</div>
          <p className="text-[13px] text-ef-textDim">Password updated! You can now sign in.</p>
          <Link
            to="/login"
            className="ef-gradient mt-5 block w-full rounded-[var(--radius)] py-[11px] text-center text-sm font-semibold text-white shadow-[0_4px_16px_rgba(34,197,176,0.3)]"
          >
            Sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell subtitle="Choose a new password (12+ chars, upper, lower, digit)">
      <form onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-ef-textDim">New password</span>
          <input
            type="password"
            required
            minLength={12}
            autoFocus
            autoComplete="new-password"
            placeholder="Min. 12 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="ef-input"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-ef-textDim">Confirm password</span>
          <input
            type="password"
            required
            minLength={12}
            autoComplete="new-password"
            placeholder="Repeat password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="ef-input"
          />
        </label>
        {confirm.length > 0 && (
          <p className={`text-xs ${password === confirm ? "text-ef-ok" : "text-ef-err"}`}>
            {password === confirm ? "Passwords match ✓" : "Passwords do not match"}
          </p>
        )}
        {error && (
          <div className="rounded-[var(--radius-sm)] border border-ef-err/20 bg-ef-err/[0.08] px-3 py-2 text-xs text-ef-err">
            {error}
          </div>
        )}
        <AuthSubmit loading={busy} disabled={!match}>{busy ? "Updating…" : "Set new password"}</AuthSubmit>
      </form>
      <p className="mt-3 text-center text-xs text-ef-textMuted">
        <Link className="inline-flex min-h-9 items-center text-ef-accent hover:underline" to="/login">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
